<script setup>
/* global axios */
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const route = useRoute();
const loading = ref(false);
const qr = ref('');
const connected = ref(false);
const error = ref('');

const accountId = computed(() => route.params.accountId);

const refreshQr = async () => {
  loading.value = true;
  error.value = '';

  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/whatsapp_qr`
    );
    qr.value = data.qr || '';
    connected.value = Boolean(data.connected && !data.qr);
  } catch (requestError) {
    error.value =
      requestError?.response?.data?.error ||
      'Não foi possível gerar o QR agora.';
  } finally {
    loading.value = false;
  }
};

onMounted(refreshQr);
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <BaseSettingsHeader
      title="WhatsApp QR"
      description="Conecte um número via WhatsApp Web sem expor a chave da Evolution API."
    >
      <template #actions>
        <Button
          :label="loading ? 'Gerando...' : 'Gerar / Atualizar QR'"
          icon="i-lucide-refresh-cw"
          size="sm"
          :disabled="loading"
          @click="refreshQr"
        />
      </template>
    </BaseSettingsHeader>

    <div
      class="flex flex-col items-center justify-center gap-5 p-8 border rounded-xl bg-n-solid-1 border-n-weak"
    >
      <template v-if="connected">
        <div
          class="flex items-center justify-center w-16 h-16 rounded-full bg-n-alpha-2"
        >
          <span class="text-3xl i-lucide-circle-check text-n-teal-10" />
        </div>
        <div class="text-center">
          <h3 class="text-xl font-medium text-n-slate-12">
            WhatsApp conectado
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            Esta instância já está conectada e pronta para uso.
          </p>
        </div>
      </template>

      <template v-else-if="qr">
        <div class="text-center">
          <h3 class="text-xl font-medium text-n-slate-12">
            Escaneie o QR Code
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            WhatsApp → Dispositivos conectados → Conectar dispositivo
          </p>
        </div>

        <div class="p-4 bg-white rounded-xl">
          <img
            :src="qr"
            alt="QR Code do WhatsApp"
            class="w-[340px] max-w-full h-auto"
          />
        </div>

        <p class="max-w-xl text-sm text-center text-n-slate-10">
          Se o QR expirar, clique em “Gerar / Atualizar QR”.
        </p>
      </template>

      <template v-else-if="error">
        <div class="text-center">
          <h3 class="text-lg font-medium text-n-slate-12">
            Não foi possível gerar o QR
          </h3>
          <p class="mt-2 text-sm text-n-ruby-10">{{ error }}</p>
        </div>
      </template>

      <template v-else>
        <p class="text-sm text-n-slate-11">
          {{ loading ? 'Gerando QR...' : 'Clique para gerar um QR.' }}
        </p>
      </template>
    </div>
  </div>
</template>
